import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X } from 'lucide-react';
import { navLinks } from '../../data/mock';

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[999] transition-all duration-300 scheme-dark"
      style={{
        height: '72px',
        background: scrolled ? 'rgba(7, 10, 18, 0.9)' : 'rgba(7, 10, 18, 0.55)',
        backdropFilter: 'blur(18px) saturate(150%)',
        WebkitBackdropFilter: 'blur(18px) saturate(150%)',
        borderBottom: scrolled ? '1px solid rgba(34, 211, 238, 0.18)' : '1px solid rgba(255, 255, 255, 0.06)'
      }}
    >
      <div
        aria-hidden="true"
        className="absolute left-0 right-0 top-0 h-[2px]"
        style={{ background: 'linear-gradient(90deg, transparent 0%, #22D3EE 25%, #A78BFA 55%, #34D399 80%, transparent 100%)', opacity: scrolled ? 0.9 : 0.55 }}
      />
      <div className="w-full px-4 md:px-6 h-full flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 no-underline">
          <span className="relative inline-flex">
            <span className="absolute inset-0 rounded-full blur-md" style={{ background: 'rgba(34,211,238,0.35)' }} aria-hidden="true" />
            <img
              src="/icons/globistaan-live-logo.png"
              alt="Globistaan logo"
              className="relative h-9 w-auto object-contain"
            />
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-xl font-bold" style={{ color: '#ffffff', lineHeight: 1, letterSpacing: '-0.01em', fontFamily: 'var(--font-display)' }}>
              Globistaan
            </span>
            <span
              className="mt-1 text-[0.6rem] font-medium uppercase"
              style={{ color: 'rgba(255,255,255,0.66)', letterSpacing: '0.08em', lineHeight: 1.15, fontFamily: 'var(--font-mono)' }}
            >
              Technologies and Exports Pvt Ltd
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 relative">
          {navLinks.map((link) => {
            const active = location.pathname === link.path;
            return (
              <Link key={link.path} to={link.path} className={`nav-link relative ${active ? 'active' : ''}`}>
                {active && (
                  <motion.span layoutId="nav-indicator" className="nav-indicator" transition={{ type: 'spring', stiffness: 380, damping: 32 }} />
                )}
                <span className="relative z-10">{link.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            className="header-desktop-cta btn-primary btn-accent text-xs"
            style={{ padding: '0.55rem 1.2rem', minHeight: '2.25rem', textDecoration: 'none' }}
          >
            Get in Touch
          </Link>
          <button
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            className="lg:hidden w-9 h-9 rounded-full flex items-center justify-center border transition-colors duration-200"
            style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.18)', color: '#ffffff', cursor: 'pointer' }}
          >
            {isOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden"
            style={{ background: 'rgba(7, 10, 18, 0.98)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(34, 211, 238, 0.18)' }}
          >
            <nav className="flex flex-col px-5 py-4 gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                  style={{ borderRadius: '0.6rem', padding: '0.8rem 1rem', fontSize: '0.85rem', background: location.pathname === link.path ? 'rgba(34,211,238,0.12)' : undefined }}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/contact" className="btn-primary btn-accent text-xs mt-3" style={{ textDecoration: 'none' }}>
                Get in Touch
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
